'use client'

import React from 'react'
import { cn } from '../../helpers/index.js'
import type { MediaDrawAreaProps, MediaConfig, ResponsiveMedia } from './types.js'
import { DEFAULT_CTA_BUTTON } from './types.js'

const BREAKPOINTS = {
  tablet: '(min-width: 768px)',
  laptop: '(min-width: 1025px)',
  desktop: '(min-width: 1440px)',
  lgDesktop: '(min-width: 1920px)',
} as const

function isResponsiveMedia(src: string | ResponsiveMedia): src is ResponsiveMedia {
  return typeof src === 'object' && 'default' in src
}

function getDefaultSrc(media: MediaConfig): string {
  if (typeof media.src === 'string') return media.src
  return media.src.default
}

const ResponsiveImage: React.FC<{
  media: MediaConfig
  className?: string
}> = ({ media, className }) => {
  const alt = media.alt || 'Product Lottery'

  if (!isResponsiveMedia(media.src)) {
    return <img src={media.src} alt={alt} className={cn('size-full object-contain', className)} loading="lazy" />
  }

  const responsive = media.src

  return (
    <picture>
      {responsive.lgDesktop && <source media={BREAKPOINTS.lgDesktop} srcSet={responsive.lgDesktop} />}
      {responsive.desktop && <source media={BREAKPOINTS.desktop} srcSet={responsive.desktop} />}
      {responsive.laptop && <source media={BREAKPOINTS.laptop} srcSet={responsive.laptop} />}
      {responsive.tablet && <source media={BREAKPOINTS.tablet} srcSet={responsive.tablet} />}
      <img src={responsive.default} alt={alt} className={cn('size-full object-cover', className)} loading="lazy" />
    </picture>
  )
}

const VideoMedia: React.FC<{
  media: MediaConfig
  className?: string
}> = ({ media, className }) => {
  const src = getDefaultSrc(media)

  return (
    <video
      src={src}
      poster={media.poster}
      autoPlay={media.autoPlay ?? true}
      muted={media.muted ?? true}
      loop={media.loop ?? true}
      playsInline
      className={cn('size-full object-cover', className)}
      aria-label={media.alt || 'Product Lottery Video'}
    />
  )
}

const CTA_STYLE_MAP = {
  primary: cn('bg-btn-primary text-btn-primary-foreground', 'shadow-lg'),
  secondary: cn('bg-[#1D1D1F] text-white', 'shadow-lg hover:bg-[#1D1D1F]/90'),
  outline: cn('border-2 border-white bg-transparent text-white', 'hover:bg-white/10'),
} as const

export const MediaDrawArea: React.FC<MediaDrawAreaProps> = ({
  media,
  ctaButton,
  isDrawing = false,
  isDisabled = false,
  onClick,
  className,
}) => {
  const buttonText = ctaButton?.text ?? ''
  const buttonStyle = ctaButton?.style || 'primary'

  return (
    <div className={cn('media-draw-area', 'size-full overflow-hidden', className)}>
      {/* Media Layer */}
      <div className="absolute inset-0">
        {media.type === 'video' ? <VideoMedia media={media} /> : <ResponsiveImage media={media} />}
      </div>

      {/* Clickable overlay — entire area triggers draw */}
      <button
        type="button"
        onClick={onClick}
        disabled={isDisabled || isDrawing}
        className={cn(
          'absolute inset-0 z-10 flex cursor-pointer items-end justify-center',
          'tablet:pb-5 laptop:pb-6 desktop:pb-8 lg-desktop:pb-10 pb-4',
          'disabled:cursor-not-allowed',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2'
        )}
        aria-label={isDrawing ? 'Drawing in progress...' : buttonText}
        aria-busy={isDrawing}
      >
        {/* CTA Button visual — hidden when no text configured */}
        {buttonText && (
          <span
            className={cn(
              'inline-flex items-center justify-center gap-2',
              'rounded-btn px-6 py-2.5',
              'text-sm font-semibold',
              'laptop:px-8 laptop:py-3 laptop:text-base',
              'desktop:px-10 desktop:py-3.5',
              'transition-all duration-200',
              'hover:scale-105 active:scale-95',
              CTA_STYLE_MAP[buttonStyle],
              isDrawing && 'pointer-events-none opacity-80',
              isDisabled && 'pointer-events-none opacity-50'
            )}
          >
            {isDrawing && (
              <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="opacity-25"
                />
                <path
                  d="M4 12a8 8 0 018-8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="opacity-75"
                />
              </svg>
            )}
            {buttonText}
          </span>
        )}
      </button>
    </div>
  )
}

MediaDrawArea.displayName = 'MediaDrawArea'

export default MediaDrawArea
